import { Box } from '@mui/material';
import type { ReactElement } from 'react';

import type { ColorTokens } from '../tokens';
import { displayValue, type DisplayableValue } from './displayValue';

/**
 * Pastille d'etat ou de compte (maquette « BO/Badge », six tons).
 *
 * Elle remplace les `Chip` MUI colories a la main (`StateChips` des comptes,
 * listes de valeurs des ressources) : un `Chip` est un controle — il se
 * supprime, se clique —, une pastille ne fait que **dire**.
 *
 * **Le libelle porte le sens, jamais la couleur seule** (WCAG 1.4.1) : le ton
 * double l'information pour l'oeil, il ne la remplace pas. D'ou un libelle
 * obligatoire ; une valeur absente s'affiche « — » et non une pastille vide ou
 * un zero invente.
 *
 * Les couples encre/aplat sont ceux de la maquette, chacun garde a 4,5:1 dans
 * les deux modes par `tokens.contrast.test.ts`.
 */
export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand';

export const BADGE_TONES: Readonly<Record<BadgeTone, { readonly ink: keyof ColorTokens; readonly fill: keyof ColorTokens }>> = {
  neutral: { ink: 'textMuted', fill: 'neutralSoft' },
  success: { ink: 'successText', fill: 'successSoft' },
  warning: { ink: 'accentYellowStrong', fill: 'accentYellowSoft' },
  danger: { ink: 'criticalText', fill: 'criticalSoft' },
  info: { ink: 'infoText', fill: 'infoSoft' },
  brand: { ink: 'primary', fill: 'primarySoft' },
};

export interface BadgeProps {
  readonly label: DisplayableValue;
  readonly tone?: BadgeTone;
  /** Point devant le libelle, pour un etat « en cours » ou « a traiter ». */
  readonly dot?: boolean;
}

export const Badge = ({ label, tone = 'neutral', dot = false }: BadgeProps): ReactElement => {
  const { ink, fill } = BADGE_TONES[tone];

  return (
    <Box
      component="span"
      data-tone={tone}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1,
        py: '3px',
        borderRadius: 999,
        bgcolor: `tokens.${fill}`,
        color: `tokens.${ink}`,
        fontSize: 12,
        fontWeight: 700,
        lineHeight: '16px',
        whiteSpace: 'nowrap',
      }}
    >
      {dot ? (
        <Box
          component="span"
          aria-hidden
          sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor', flexShrink: 0 }}
        />
      ) : null}
      {displayValue(label)}
    </Box>
  );
};
